<template>
  <section
    v-if="supported"
    class="reference-settings my-2"
    :aria-labelledby="`reference-title-${huayId}`"
    :aria-busy="loading || saving"
  >
    <div class="reference-settings-header">
      <div>
        <h5
          :id="`reference-title-${huayId}`"
          class="mb-25"
        >
          {{ $t('แหล่งอ้างอิงผลหวย') }}
        </h5>
        <p class="mb-0 font-small-3">
          {{ $t('ตรวจเลขก่อนคำนวณและจ่ายเครดิต') }}
        </p>
      </div>
      <div
        v-if="setting && !loading"
        class="reference-settings-toggle"
      >
        <span class="d-none d-sm-inline font-small-3">{{ $t(setting.enabled ? 'เปิดใช้งาน' : 'ปิดอยู่') }}</span>
        <b-form-checkbox
          v-model="setting.enabled"
          switch
          class="m-0"
          :disabled="!canEdit || saving"
          @change="changed"
        >
          <span class="sr-only">{{ $t('ตรวจผลกับแหล่งอ้างอิงก่อนคำนวณและจ่ายเครดิต') }}</span>
        </b-form-checkbox>
      </div>
    </div>

    <div
      v-if="loading"
      class="reference-settings-body"
      role="status"
    >
      <b-spinner
        small
        class="mr-50"
      />
      {{ $t('กำลังโหลดข้อมูล') }}
    </div>
    <div
      v-else-if="setting"
      class="reference-settings-body"
      role="group"
      :aria-labelledby="`reference-sources-${huayId}`"
      :aria-describedby="`reference-required-${huayId}`"
      :aria-invalid="invalidSelection"
    >
      <div class="d-flex justify-content-between align-items-center mb-25">
        <strong :id="`reference-sources-${huayId}`">{{ $t('แหล่งข้อมูลที่ใช้ตรวจ') }}</strong>
        <small class="text-nowrap ml-50">{{ $t('เลือกแล้ว {count} แหล่ง', { count: setting.sourceIds.length }) }}</small>
      </div>
      <p
        :id="`reference-required-${huayId}`"
        class="font-small-3 mb-75"
        :class="{ 'text-danger': invalidSelection }"
      >
        {{ $t('เมื่อเปิดใช้งาน ต้องเลือกอย่างน้อย 1 แหล่ง') }}
      </p>
      <div
        v-for="source in sources"
        :key="source.id"
        class="reference-source mb-50"
        :class="{ 'is-selected': setting.sourceIds.includes(source.id), 'is-disabled': !canEdit || saving }"
      >
        <b-form-checkbox
          v-model="setting.sourceIds"
          :value="source.id"
          :disabled="!canEdit || saving"
          class="reference-source-checkbox pl-0"
          @change="changed"
        >
          <span class="d-block font-weight-bold">{{ source.name }}</span>
          <small class="d-block">{{ sourceHost(source.url) }}</small>
        </b-form-checkbox>
        <a
          class="reference-source-link"
          :href="source.url"
          :aria-label="$t('เปิดเว็บไซต์ {name}', { name: source.name })"
          :title="$t('เปิดเว็บไซต์ {name}', { name: source.name })"
          target="_blank"
          rel="noopener noreferrer"
        >
          <feather-icon
            icon="ExternalLinkIcon"
            size="16"
          />
        </a>
      </div>
      <p class="reference-settings-hint mt-1 mb-0 font-small-3">
        <feather-icon
          icon="InfoIcon"
          size="15"
        />
        <span>{{ $t('เลขไม่ตรงต้องระบุเหตุผล · ดึงไม่ได้ต้องยืนยันเลขอีกครั้ง') }}</span>
      </p>
    </div>

    <div
      v-if="canEdit && setting && !loading"
      class="reference-settings-footer"
    >
      <span
        class="reference-settings-hint reference-settings-status font-small-3"
        :class="{ 'text-danger': !!error, 'text-success': saved && !isDirty }"
        role="status"
      >
        <feather-icon
          :icon="statusIcon"
          size="15"
        />
        {{ $t(statusText) }}
      </span>
      <div class="reference-settings-actions">
        <b-button
          variant="outline-secondary"
          size="sm"
          :disabled="saving || !isDirty"
          @click="reset"
        >
          {{ $t('Cancel') }}
        </b-button>
        <b-button
          variant="primary"
          size="sm"
          :disabled="saving || !isDirty || invalidSelection"
          :aria-busy="saving"
          @click="save"
        >
          <b-spinner
            v-if="saving"
            small
            class="mr-50"
            aria-hidden="true"
          />
          <feather-icon
            v-else
            icon="SaveIcon"
            size="14"
            class="mr-50"
          />
          {{ $t('บันทึก') }}
        </b-button>
      </div>
    </div>
    <div
      v-if="error"
      class="reference-settings-body"
    >
      <b-alert
        show
        variant="danger"
        class="mb-0"
      >
        <div class="alert-body">
          {{ error }}
          <b-button
            v-if="!setting"
            variant="outline-danger"
            size="sm"
            class="ml-1"
            @click="load"
          >
            {{ $t('ลองอีกครั้ง') }}
          </b-button>
        </div>
      </b-alert>
    </div>
  </section>
</template>

<script>
import {
  BAlert, BButton, BFormCheckbox, BSpinner,
} from 'bootstrap-vue'
import { referenceRequest, supportedLottery } from '@/service/resultReference'

export default {
  components: {
    BAlert, BButton, BFormCheckbox, BSpinner,
  },
  props: { huayId: { type: String, required: true } },
  data: () => ({
    setting: null, savedSetting: null, sources: [], loading: false, saving: false, error: '', saved: false,
  }),
  computed: {
    supported() { return supportedLottery(this.huayId) },
    canEdit() { return this.$can('update', 'lotto.info') },
    invalidSelection() { return !!this.setting && this.setting.enabled && !this.setting.sourceIds.length },
    statusText() {
      if (this.saving) return 'กำลังบันทึก'
      if (this.error) return 'บันทึกไม่สำเร็จ'
      if (this.isDirty) return 'ยังไม่ได้บันทึก'
      return this.saved ? 'บันทึกแล้ว' : 'ไม่มีการแก้ไข'
    },
    statusIcon() {
      if (this.error) return 'AlertCircleIcon'
      return this.saved && !this.isDirty ? 'CheckCircleIcon' : 'Edit3Icon'
    },
    isDirty() {
      return this.setting && this.savedSetting && (
        this.setting.enabled !== this.savedSetting.enabled
        || this.setting.sourceIds.length !== this.savedSetting.sourceIds.length
        || this.setting.sourceIds.some(id => !this.savedSetting.sourceIds.includes(id))
      )
    },
  },
  watch: { huayId: { immediate: true, handler: 'load' } },
  methods: {
    sourceHost(url) { return new URL(url).hostname.replace(/^www\./, '') },
    changed() {
      this.saved = false
      this.error = ''
    },
    applySetting(setting) {
      this.setting = { ...setting, sourceIds: [...setting.sourceIds] }
      this.savedSetting = { ...setting, sourceIds: [...setting.sourceIds] }
    },
    reset() {
      this.applySetting(this.savedSetting)
      this.changed()
    },
    async load() {
      const id = this.huayId
      this.setting = null
      this.savedSetting = null
      this.changed()
      if (!this.supported) return
      this.loading = true
      try {
        const data = await referenceRequest('get', `settings/${encodeURIComponent(id)}`)
        if (id !== this.huayId) return
        this.applySetting(data.setting)
        this.sources = data.sources
      } catch (error) {
        if (id === this.huayId) this.error = error.message
      } finally {
        if (id === this.huayId) this.loading = false
      }
    },
    async save() {
      if (!this.canEdit || this.saving || !this.isDirty) return
      this.changed()
      if (this.invalidSelection) return
      const id = this.huayId
      this.saving = true
      try {
        const setting = await referenceRequest('put', `settings/${encodeURIComponent(id)}`, this.setting)
        if (id !== this.huayId) return
        this.applySetting(setting)
        this.saved = true
      } catch (error) {
        if (id === this.huayId) this.error = error.message
      } finally { this.saving = false }
    },
  },
}
</script>

<style lang="scss" scoped>
.reference-settings {
  border: 1px solid var(--hdg-border);
  border-radius: 0.5rem;
  background: var(--hdg-panel);
  color: var(--hdg-body);
}

.reference-settings-header,
.reference-settings-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  padding: 1.25rem;
}

.reference-settings-toggle,
.reference-settings-actions,
.reference-settings-hint {
  display: flex;
  align-items: center;
  gap: 0.5rem;

  svg { flex-shrink: 0; }
}

.reference-settings-toggle { white-space: nowrap; }
.reference-settings-toggle ::v-deep .custom-control-label { margin-bottom: 0; }
.reference-settings-body { padding: 0 1.25rem 1.25rem; }
.reference-settings-footer {
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  border-top: 1px solid var(--hdg-border);
}
.reference-settings-status { min-height: 1.5rem; white-space: nowrap; }
.reference-settings-actions { margin-left: auto; }

@media (max-width: 575.98px) {
  .reference-settings-footer { grid-template-columns: 1fr; }
}

.reference-source {
  position: relative;
  border: 1px solid var(--hdg-border);
  border-radius: 0.375rem;

  &:focus-within,
  &.is-selected { border-color: var(--hdg-brand-primary); }
  &.is-selected { background: var(--hdg-brand-hover-bg); }
  &:not(.is-disabled):hover { background: var(--hdg-brand-hover-bg); }
  &.is-disabled ::v-deep .custom-control-label { cursor: default; }
}

.reference-source-checkbox {
  ::v-deep .custom-control-label {
    display: block;
    padding: 0.75rem 2.75rem 0.75rem 2.5rem;
    margin-bottom: 0;
    cursor: pointer;
    color: var(--hdg-body);

    &::before,
    &::after { top: 50%; left: 0.75rem; transform: translateY(-50%); }
  }
}

.reference-source-link {
  position: absolute;
  right: 0.5rem;
  top: 50%;
  transform: translateY(-50%);
  display: flex;
  align-items: center;
  justify-content: center;
  width: 2.25rem;
  height: 2.25rem;
  border-radius: 0.25rem;
  color: var(--hdg-body);

  &:hover,
  &:focus-visible { color: var(--hdg-brand-primary); background: var(--hdg-brand-hover-bg); }
}
</style>
